import { Textbit, type PendingDrop } from '@ttab/textbit'
import type { JSX } from 'react'
import { LoaderIcon } from '@ttab/elephant-ui/icons'

/**
 * Transient marker shown while a dropped/pasted resource is being consumed
 * (e.g. an image upload). Purely ephemeral - it lives in local React state
 * plus Yjs awareness and never becomes part of the saved document.
 *
 * Own drops show a spinner; peer drops (received via awareness) show the
 * peer's name/colour so collaborators see where an upload is landing. The
 * marker reserves real flow space so content below shifts down while it runs.
 */
export const UploadMarker = (): JSX.Element => {
  return (
    <Textbit.UploadMarker
      height={72}
      className='flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400'
    >
      {(drop) => (drop.source === 'peer' ? <PeerLabel drop={drop} /> : <OwnLabel />)}
    </Textbit.UploadMarker>
  )
}

const OwnLabel = (): JSX.Element => (
  <LoaderIcon size={16} strokeWidth={1.75} className='animate-spin' />
)

interface PeerData {
  name?: string
  color?: string
}

function PeerLabel({ drop }: { drop: PendingDrop }): JSX.Element {
  const peer = isPeerData(drop.peerData) ? drop.peerData : undefined
  return (
    <span
      className='flex items-center gap-2'
      style={peer?.color ? { color: peer.color } : undefined}
    >
      <LoaderIcon size={16} strokeWidth={1.75} className='animate-spin' />
      {peer?.name ? <span>{peer.name}</span> : null}
    </span>
  )
}

function isPeerData(value: unknown): value is PeerData {
  return typeof value === 'object' && value !== null
}
